import { NavLink } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import type { PermissionKey } from '@shared/rbac';

interface NavItem {
  label: string;
  path: string;
  icon: string;
  /** Permission required to see this nav item. `null` = always visible. */
  permission: PermissionKey | null;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: 'dashboard', permission: null },
  { label: 'Customers', path: '/customers', icon: 'group', permission: 'customers:list' },
  { label: 'Subscriptions', path: '/subscriptions', icon: 'sync', permission: 'subscriptions:list' },
  { label: 'Invoices', path: '/invoices', icon: 'description', permission: 'invoices:list' },
  { label: 'Transactions', path: '/transactions', icon: 'receipt_long', permission: 'transactions:list' },
  { label: 'Payment Methods', path: '/payment-methods', icon: 'credit_card', permission: 'payment_methods:view' },
  { label: 'Collections', path: '/collections', icon: 'collections_bookmark', permission: 'collections:list' },
  { label: 'Dunning', path: '/dunning', icon: 'autorenew', permission: 'dunning:view' },
  { label: 'Alerts', path: '/alerts', icon: 'notifications_active', permission: 'alerts:list' },
  { label: 'Tenants', path: '/tenants', icon: 'apartment', permission: 'tenants:list' },
];

const BOTTOM_ITEMS: NavItem[] = [
  { label: 'Users', path: '/users', icon: 'people', permission: 'users:list' },
  { label: 'Settings', path: '/settings', icon: 'settings', permission: 'settings:view' },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { user, checkPermission } = useAuth();
  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : 'JD';
  const displayName = user ? `${user.firstName} ${user.lastName}` : 'John Doe';
  const displayRole = user?.role
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1) + ' Account'
    : 'Admin Account';

  const linkClass = (isActive: boolean) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-white/10 text-white'
        : 'text-white/70 hover:bg-white/5 hover:text-white'
    }${collapsed ? ' justify-center' : ''}`;

  /** Show item when no permission is required or the user has the permission. */
  const isVisible = (item: NavItem): boolean =>
    item.permission === null || checkPermission(item.permission);

  const visibleNavItems = NAV_ITEMS.filter(isVisible);
  const visibleBottomItems = BOTTOM_ITEMS.filter(isVisible);

  return (
    <aside
      className={`flex flex-col bg-sidebar shrink-0 transition-all duration-200 ${collapsed ? 'w-16' : 'w-64'}`}
    >
      {/* Logo / brand */}
      <div className="p-6 flex items-center gap-3">
        <div className="bg-white/20 p-2 rounded-lg">
          <span className="material-symbols-outlined text-white text-2xl">account_balance_wallet</span>
        </div>
        {!collapsed && (
          <h1 className="text-white text-xl font-bold tracking-tight">XS+ Billing</h1>
        )}
      </div>

      {/* Main navigation */}
      <nav className="flex-1 px-4 space-y-1 mt-4" aria-label="Main navigation">
        {visibleNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) => linkClass(isActive)}
            title={collapsed ? item.label : undefined}
          >
            <span className="material-symbols-outlined">{item.icon}</span>
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}

        {/* Bottom nav items */}
        <div className="pt-8 space-y-1">
          {visibleBottomItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => linkClass(isActive)}
              title={collapsed ? item.label : undefined}
            >
              <span className="material-symbols-outlined">{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* User profile */}
      <div className="p-4 border-t border-white/10">
        <button
          type="button"
          onClick={onToggle}
          className={`flex items-center gap-3 px-2 py-2 w-full rounded-lg hover:bg-white/5 transition-colors${collapsed ? ' justify-center' : ''}`}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <div className="size-8 rounded-full bg-primary flex items-center justify-center text-xs font-bold text-white">
            {initials}
          </div>
          {!collapsed && (
            <div className="flex flex-col text-left">
              <span className="text-xs font-semibold text-white">{displayName}</span>
              <span className="text-[10px] text-white/50">{displayRole}</span>
            </div>
          )}
        </button>
      </div>
    </aside>
  );
}
